import React, { FC } from 'react';
import { useTranslate } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import InfoIcon from '@material-ui/icons/Info';

import { TextField, StatusField } from '~/components';
import { TimeIcon, RibbonIcon, PersonIcon } from '~/img';
import { AsideBase } from '~/layout';
import { ApprovalCaseDetail } from '~/types/approvalCase';
import { time } from '~/utils';
import { getTypeOfIdentity } from '~/utils/identities';
import palette from '~/theme/palette';
import longdash from '~/utils/Longdash';
import ReferenceField from '~/components/ReferenceField';

const dateFormat = (iso: string) => time(iso).format('ll, LTS');

interface Props {
  record: ApprovalCaseDetail;
}

export const ApprovalAside: FC<Props> = ({ record }) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.approvals.aside.${key}`);
  const tActionType = (key: string) =>
    translate(`resources.approvals.list.filters.actionTypeOptions.${key}`);

  const {
    status,
    submittedAt,
    expiresAt,
    completedAt,
    actionType,
    claimedByIdentityId,
    claimedByIdentityType,
    executionResult,
  } = record;
  const isInProgress = status === 'IN_PROGRESS';
  const isFailed = status === 'FAILED';

  return (
    <AsideBase>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField label={t('submittedAt')} icon={<TimeIcon />}>
            {dateFormat(submittedAt)}
          </TextField>
        </Grid>
        {isInProgress && expiresAt && (
          <Grid item xs={12}>
            <TextField label={t('expiresAt')} icon={<EventAvailableIcon />}>
              <span>{dateFormat(expiresAt)}</span>
              <ExpiryHint expiresAt={expiresAt} />
            </TextField>
          </Grid>
        )}
        {!isInProgress && completedAt && (
          <Grid item xs={12}>
            <TextField label={t('completedAt')} icon={<EventAvailableIcon />}>
              {dateFormat(completedAt)}
            </TextField>
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField label={t('actionType')} icon={<InfoIcon />}>
            {tActionType(actionType)}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField label={t('status')} icon={<RibbonIcon />}>
            <StatusField status={status} />
          </TextField>
        </Grid>
        {isFailed && executionResult?.message && (
          <Grid item xs={12}>
            <TextField label={t('reason')} icon={<></>} textWrap>
              {executionResult.message}
            </TextField>
          </Grid>
        )}
        {isInProgress && (
          <Grid item xs={12}>
            {claimedByIdentityId && claimedByIdentityType ? (
              <ReferenceField
                withLink={false}
                withDefaultIcon
                label={t('claimedBy')}
                icon={<PersonIcon />}
                record={{
                  id: claimedByIdentityId,
                  type: getTypeOfIdentity(claimedByIdentityType),
                }}
              />
            ) : (
              <TextField label={t('claimedBy')} icon={<PersonIcon />}>
                {longdash}
              </TextField>
            )}
          </Grid>
        )}
      </Grid>
    </AsideBase>
  );
};

const ExpiryHint: FC<{ expiresAt: string }> = ({ expiresAt }) => {
  const diff = time(expiresAt).diff(time(), 'minutes');
  const color =
    diff < 0 ? palette.error.main : diff < 60 ? palette.warning.main : palette.text.secondary;
  return (
    <Typography variant='caption' style={{ color, display: 'block', marginTop: 2 }}>
      {time(expiresAt).fromNow()}
    </Typography>
  );
};
